export type DeathCause = "pvp" | "env";

export type DropMode = "PreservedValue" | "LifetimeEarned";

export type EconomyLedger = {
  walletMana: number;
  aliveUnitValue: number;
  specialResidualValue: number;
  lifetimeEarnedMana: number;
};

export function preservedValue(ledger: EconomyLedger): number {
  return ledger.walletMana + ledger.aliveUnitValue + ledger.specialResidualValue;
}

export function masterDropAmount(
  ledger: EconomyLedger,
  cause: DeathCause,
  opts: {
    dropMode: DropMode;
    pvpRate: number;
    envRate: number;
    maxCap: number;
  },
): number {
  const rate = cause === "pvp" ? opts.pvpRate : opts.envRate;
  const base =
    opts.dropMode === "LifetimeEarned"
      ? ledger.lifetimeEarnedMana
      : preservedValue(ledger);
  const raw = Math.floor(base * rate);
  return Math.max(0, Math.min(opts.maxCap, raw));
}

/** Split total into orbs each ≤ maxOrbValue; sum equals total. */
export function splitDropOrbs(total: number, maxOrbValue: number): number[] {
  if (total <= 0) return [];
  const max = Math.max(1, Math.floor(maxOrbValue));
  const orbs: number[] = [];
  let left = total;
  while (left > 0) {
    const v = Math.min(max, left);
    orbs.push(v);
    left -= v;
  }
  return orbs;
}

export function unitKillReturn(summonCost: number, returnRate: number): number {
  return Math.floor(summonCost * returnRate);
}

export function specialResidualFromCost(
  manaEquivalentCost: number,
  residualRate: number,
): number {
  return Math.floor(manaEquivalentCost * residualRate);
}

export function sigilsFromLifetime(
  lifetimeEarned: number,
  every: number,
): number {
  if (every <= 0) return 0;
  return Math.floor(lifetimeEarned / every);
}

/** Apply summon: wallet → alive unit value (no world value change). */
export function applyTroopSummon(
  ledger: EconomyLedger,
  cost: number,
): EconomyLedger | null {
  if (ledger.walletMana < cost) return null;
  return {
    ...ledger,
    walletMana: ledger.walletMana - cost,
    aliveUnitValue: ledger.aliveUnitValue + cost,
  };
}

export function applyTroopDeath(
  ledger: EconomyLedger,
  cost: number,
): EconomyLedger {
  return {
    ...ledger,
    aliveUnitValue: Math.max(0, ledger.aliveUnitValue - cost),
  };
}

export function applyManaGain(
  ledger: EconomyLedger,
  amount: number,
): EconomyLedger {
  if (amount <= 0) return ledger;
  return {
    ...ledger,
    walletMana: ledger.walletMana + amount,
    lifetimeEarnedMana: ledger.lifetimeEarnedMana + amount,
  };
}

export function clearArmyValueOnMasterDeath(
  ledger: EconomyLedger,
): EconomyLedger {
  return {
    ...ledger,
    walletMana: 0,
    aliveUnitValue: 0,
    specialResidualValue: 0,
  };
}
